// src/components/ProfilePhotoControl.js
//
// The Account-page avatar as a control (docs/ux/profile-photo.md):
//   no photo  → tap opens the OS picker (library / camera / files)
//   has photo → tap opens a small menu: Choose a new photo · Remove photo
//   desktop   → drag an image onto the avatar area
// Picking a file opens ProfilePhotoFramer; saving uploads the crop to Clerk and
// records it in memberProfiles (the display index other members read).
//
// Ordering is deliberate:
//   save   = Clerk upload → reload → memberProfiles (photo only becomes public
//            once Clerk actually has it)
//   remove = memberProfiles FIRST (hides it from everyone at once) → Clerk.
//            If Clerk removal then fails the photo is already invisible, which
//            is the outcome the member asked for.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ProfilePhotoFramer from './ProfilePhotoFramer';
import { fetchOwnMemberPhoto, saveMemberPhoto } from '../services/memberProfiles';

const MAX_INPUT_BYTES = 25 * 1024 * 1024;

const DEFAULT_COPY = {
    addLabel: 'Add a photo',
    frameTitle: 'Frame your photo',
    frameHint: 'Drag to move · pinch or slide to zoom',
    previewLabel: 'How others will see you',
    useLabel: 'Use photo',
    savingLabel: 'Saving…',
    chooseAnotherLabel: 'Choose a different photo',
    savedLabel: 'Looking good.',
    changeLabel: 'Choose a new photo',
    removeLabel: 'Remove photo',
    removeConfirm: 'Remove your photo? You’ll show as your initial again.',
    removeConfirmYes: 'Remove',
    removeConfirmNo: 'Keep it',
    errorSave: 'Couldn’t save your photo. Check your connection and try again.',
    errorFormat: 'That photo format isn’t supported here. Try a JPG or PNG.',
    errorTooLarge: 'That photo is too large — try a smaller one.',
    errorNotImage: 'Drop a photo (JPG or PNG).',
    errorRemove: 'Couldn’t remove it — try again.',
};

export function profilePhotoCopy(c = {}) {
    const pick = (key, fallback) => (typeof c[key] === 'string' && c[key].trim() ? c[key] : fallback);
    return {
        ...DEFAULT_COPY,
        addLabel: pick('profilePhotoAddLabel', DEFAULT_COPY.addLabel),
        frameTitle: pick('profilePhotoFrameTitle', DEFAULT_COPY.frameTitle),
        frameHint: pick('profilePhotoFrameHint', DEFAULT_COPY.frameHint),
        previewLabel: pick('profilePhotoPreviewLabel', DEFAULT_COPY.previewLabel),
        useLabel: pick('profilePhotoUseLabel', DEFAULT_COPY.useLabel),
        chooseAnotherLabel: pick('profilePhotoChooseAnotherLabel', DEFAULT_COPY.chooseAnotherLabel),
        savedLabel: pick('profilePhotoSavedLabel', DEFAULT_COPY.savedLabel),
        changeLabel: pick('profilePhotoChangeLabel', DEFAULT_COPY.changeLabel),
        removeLabel: pick('profilePhotoRemoveLabel', DEFAULT_COPY.removeLabel),
        removeConfirm: pick('profilePhotoRemoveConfirm', DEFAULT_COPY.removeConfirm),
        errorSave: pick('profilePhotoErrorSave', DEFAULT_COPY.errorSave),
        errorFormat: pick('profilePhotoErrorFormat', DEFAULT_COPY.errorFormat),
    };
}

function MenuItem({ children, onClick, muted, primary }) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button" role="menuitem" onClick={onClick}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                display: 'block', width: '100%', textAlign: primary === undefined ? 'left' : 'center',
                padding: '13px 14px', minHeight: 44, border: 'none', borderRadius: primary === undefined ? 10 : 50,
                background: primary
                    ? 'var(--mom-charcoal)'
                    : hover ? 'color-mix(in srgb, var(--mom-charcoal) 8%, transparent)' : 'transparent',
                color: primary ? '#fff' : muted ? 'color-mix(in srgb, var(--mom-charcoal) 65%, var(--mom-cream))' : 'var(--mom-charcoal)',
                fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
        >{children}</button>
    );
}

function ProfilePhotoControl({ clerkUser, initials, content }) {
    const userId = clerkUser?.id || null;
    const copy = profilePhotoCopy(content);
    const fileRef = useRef(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [photoUrl, setPhotoUrl] = useState('');     // what's saved (Clerk URL)
    const [localUrl, setLocalUrl] = useState('');     // just-saved crop, avoids a flash while Clerk's CDN loads
    const [imgLoaded, setImgLoaded] = useState(false);
    const [framerSrc, setFramerSrc] = useState(null); // object URL of the chosen file
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [message, setMessage] = useState('');
    const [messageIsError, setMessageIsError] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [hover, setHover] = useState(false);
    const [popKey, setPopKey] = useState(0);
    const messageTimerRef = useRef(null);
    const [menuAnchor, setMenuAnchor] = useState(null); // { x, y } viewport px

    useEffect(() => {
        if (!userId) return undefined;
        let cancelled = false;
        fetchOwnMemberPhoto(userId).then((url) => { if (!cancelled) setPhotoUrl(url || ''); });
        return () => { cancelled = true; };
    }, [userId]);

    // Revoke object URLs we created.
    useEffect(() => () => { if (framerSrc) URL.revokeObjectURL(framerSrc); }, [framerSrc]);
    useEffect(() => () => { if (localUrl) URL.revokeObjectURL(localUrl); }, [localUrl]);
    useEffect(() => () => clearTimeout(messageTimerRef.current), []);

    const shownUrl = localUrl || photoUrl;
    useEffect(() => { setImgLoaded(false); }, [shownUrl]);

    const flash = useCallback((text, isError = false, ms = 3000) => {
        clearTimeout(messageTimerRef.current);
        setMessage(text); setMessageIsError(isError);
        if (ms) messageTimerRef.current = setTimeout(() => setMessage(''), ms);
    }, []);

    // Close the menu on outside click.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (menuRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
            setMenuOpen(false); setConfirmRemove(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') { setMenuOpen(false); setConfirmRemove(false); buttonRef.current?.focus(); } };
        // The menu is fixed-positioned (portal) — close rather than drift on scroll/resize.
        const onMove = () => { setMenuOpen(false); setConfirmRemove(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [menuOpen]);

    const openPicker = () => {
        setMenuOpen(false); setConfirmRemove(false);
        if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); }
    };

    const takeFile = (file) => {
        if (!file) return;
        if (!file.type || !file.type.startsWith('image/')) { flash(copy.errorNotImage, true); return; }
        if (file.size > MAX_INPUT_BYTES) { flash(copy.errorTooLarge, true); return; }
        setMessage('');
        setFramerSrc(URL.createObjectURL(file));
    };

    const closeFramer = useCallback(() => {
        setFramerSrc(null);
        setTimeout(() => buttonRef.current?.focus(), 0);
    }, []);

    const handleSave = async (blob) => {
        // Throws on failure — the framer keeps the crop and shows the error.
        await clerkUser.setProfileImage({ file: blob });
        // reload() returns the refreshed resource; fall back to the (mutated) original.
        const fresh = await clerkUser.reload();
        const url = (fresh && fresh.imageUrl) || clerkUser.imageUrl;
        await saveMemberPhoto(userId, url);
        setLocalUrl(URL.createObjectURL(blob));
        setPhotoUrl(url);
        setPopKey((k) => k + 1);
        closeFramer();
        flash(copy.savedLabel);
    };

    const handleRemove = async () => {
        const prevPhoto = photoUrl;
        setMenuOpen(false); setConfirmRemove(false);
        setPhotoUrl(''); setLocalUrl('');
        try {
            await saveMemberPhoto(userId, '');
        } catch (e) {
            // The local crop preview is gone (revoked); the Clerk URL shows the same photo.
            setPhotoUrl(prevPhoto);
            flash(copy.errorRemove, true);
            return;
        }
        try { await clerkUser.setProfileImage({ file: null }); } catch (e) { /* already hidden everywhere */ }
        buttonRef.current?.focus();
    };

    const onAvatarClick = () => {
        if (shownUrl) {
            const r = buttonRef.current?.getBoundingClientRect();
            if (r) setMenuAnchor({ x: r.left + r.width / 2, y: r.bottom + 10 });
            setMenuOpen((o) => !o); setConfirmRemove(false);
        }
        else openPicker();
    };

    const soft = 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))';

    return (
        <div
            style={{ position: 'relative', margin: '0 auto 16px' }}
            onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).indexOf('Files') !== -1) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer?.files?.[0]); }}
        >
            <button
                ref={buttonRef} type="button" onClick={onAvatarClick}
                aria-label={shownUrl ? 'Change profile photo' : 'Add profile photo'}
                aria-haspopup={shownUrl ? 'menu' : undefined}
                aria-expanded={shownUrl ? menuOpen : undefined}
                onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{
                    position: 'relative', width: 88, height: 88, padding: 0, border: 'none',
                    background: 'transparent', borderRadius: '50%', cursor: 'pointer',
                    display: 'block', margin: '0 auto',
                }}
            >
                <div key={popKey} style={{
                    width: 88, height: 88, borderRadius: '50%', overflow: 'hidden', position: 'relative',
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 25%, transparent), inset 0 1px 0 rgba(255,255,255,0.2)',
                    filter: hover ? 'brightness(0.92)' : 'none', transition: 'filter 0.15s ease',
                    outline: dragOver ? '2px dashed var(--mom-primary)' : 'none', outlineOffset: 4,
                    animation: popKey ? 'momAvatarPop 0.35s ease-out' : 'none',
                }}>
                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 30, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>{initials}</span>
                    {shownUrl && (
                        <img
                            src={shownUrl} alt="" draggable={false}
                            onLoad={() => setImgLoaded(true)}
                            onError={() => { if (shownUrl === localUrl) setLocalUrl(''); else setPhotoUrl(''); }}
                            style={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                                opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.15s ease',
                            }}
                        />
                    )}
                </div>
                <span aria-hidden="true" style={{
                    position: 'absolute', right: -2, bottom: -2, width: 30, height: 30, borderRadius: '50%',
                    background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mom-primary)',
                }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
                </span>
            </button>

            {/* Portalled to <body>: the Account card clips overflow, which would cut
                the menu off on short cards. */}
            {menuOpen && menuAnchor && createPortal(
                <div ref={menuRef} role="menu" style={{
                    position: 'fixed', left: menuAnchor.x, top: menuAnchor.y, transform: 'translateX(-50%)',
                    width: 264, maxWidth: 'calc(100vw - 48px)', background: '#fff', borderRadius: 16,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.16)', padding: 6, zIndex: 1100, textAlign: 'left',
                    animation: 'fadeIn 0.15s ease-out',
                }}>
                    {confirmRemove ? (
                        <>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, lineHeight: 1.5, color: 'var(--mom-charcoal)', margin: 0, padding: '10px 12px 6px' }}>
                                {copy.removeConfirm}
                            </p>
                            <div style={{ display: 'flex', gap: 6, padding: 6 }}>
                                <MenuItem primary onClick={handleRemove}>{copy.removeConfirmYes}</MenuItem>
                                <MenuItem primary={false} onClick={() => { setConfirmRemove(false); setMenuOpen(false); }}>{copy.removeConfirmNo}</MenuItem>
                            </div>
                        </>
                    ) : (
                        <>
                            <MenuItem onClick={openPicker}>{copy.changeLabel}</MenuItem>
                            <MenuItem onClick={() => setConfirmRemove(true)}>{copy.removeLabel}</MenuItem>
                            <MenuItem muted onClick={() => setMenuOpen(false)}>Cancel</MenuItem>
                        </>
                    )}
                </div>,
                document.body
            )}

            {!shownUrl && (
                <button
                    type="button" onClick={openPicker}
                    style={{
                        display: 'block', margin: '10px auto 0', padding: '6px 12px', minHeight: 32,
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)',
                    }}
                >{copy.addLabel}</button>
            )}
            <div role="status" aria-live="polite" style={{
                minHeight: message ? 18 : 0, marginTop: message ? 6 : 0,
                fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontStyle: messageIsError ? 'normal' : 'italic',
                color: messageIsError ? '#a3263f' : soft,
            }}>{message}</div>

            <input
                ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => takeFile(e.target.files?.[0])}
            />

            {/* Portalled to <body>: the Account card has backdrop-filter, which makes
                it the containing block for position:fixed descendants — rendered in
                place, the full-screen sheet was trapped (and clipped) inside the card. */}
            {framerSrc && createPortal(
                <ProfilePhotoFramer
                    src={framerSrc}
                    copy={copy}
                    onCancel={closeFramer}
                    onChooseAnother={openPicker}
                    onSave={handleSave}
                />,
                document.body
            )}
        </div>
    );
}

export default ProfilePhotoControl;
